import { useQueryClient } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { z } from "zod";

import { MenuItem } from "@luna-restaurant/feature-luna-school-menu-plan/build/components/MenuCard/MenuItem";
import { MenuItem as MenuItemType } from "@luna-restaurant/feature-luna-school-menu-plan/build/components/MenuCard/types";
import {
  FormDatePicker,
  FormError,
  FormProvider,
  FormTextInput,
  SubmitButton,
  useFormWrapper,
} from "@luna-restaurant/forms";
import { useTranslation } from "@luna-restaurant/i18n";
import type { SchoolGuestOrder } from "@luna-restaurant/school-api";
import {
  Button,
  NoticeBox,
  ThemedLoader,
  ThemedText,
  useCustomTheme,
} from "@luna-restaurant/ui";

import { SecondaryButton } from "@/components/SecondaryButton";
import { GuestOrderMenuQuantityInput } from "@/components/forms/GuestOrderMenuQuantityInput";
import {
  GUEST_ORDER_AMOUNT_COLUMN_SEPARATOR_OFFSET,
  GUEST_ORDER_AMOUNT_COLUMN_WIDTH,
} from "@/components/forms/guestOrderMenuLayout";
import { clampGuestOrderMenuQuantity } from "@/components/forms/guestOrderMenuQuantity";
import { useClosingTimes } from "@/hooks/useClosingTimes";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useGuestOrderMealPlan } from "@/hooks/useGuestOrderMealPlan";
import { useRequest } from "@/hooks/useRequest";
import { GuestOrder } from "@/screens/GuestOrdersScreen";
import { isOrderableDate } from "@/utils/guestOrderDeadline";

const MENU_CODE_TO_POSITION: Record<string, number> = {
  A: 0,
  B: 1,
  C: 2,
  D: 3,
};

const POSITION_TO_MENU_CODE: Record<number, string> = {
  0: "A",
  1: "B",
  2: "C",
  3: "D",
};

const createEditGuestOrderSchema = (
  t: ReturnType<typeof useTranslation>["t"],
) =>
  z.object({
    editor: z
      .string()
      .min(1, t("guestOrders.form.editorNameIsNeeded"))
      .max(255, t("guestOrders.form.editorNameTooLong"))
      .refine((val) => !/\d/.test(val), {
        error: t("guestOrders.form.editorNameCannotContainNumbers"),
      }),
    date: z.string().min(1, t("guestOrders.form.dateIsNeeded")),
    comment: z
      .string()
      .max(1000, t("guestOrders.form.commentTooLong"))
      .optional(),
  });

type EditGuestOrderFormValues = z.infer<
  ReturnType<typeof createEditGuestOrderSchema>
>;

type GuestOrderFormProps = {
  initialValues?: Partial<GuestOrder>;
  onDismiss?: () => void;
  onSubmitSuccess?: () => void;
  onSubmitError?: (error: unknown) => void;
};

export default function EditGuestOrderForm({
  initialValues,
  onDismiss,
  onSubmitSuccess,
  onSubmitError,
}: GuestOrderFormProps) {
  const { t, i18n } = useTranslation();
  const { colors, fonts } = useCustomTheme();
  const request = useRequest();
  const { currentUser } = useCurrentUser();
  const queryClient = useQueryClient();
  const { items: closingTimes } = useClosingTimes({
    type: "PUBLIC_HOLIDAY",
    fetchAllPages: true,
  });

  const [selectedMenusCounter, setSelectedMenusCounter] = useState<
    Record<string, number>
  >({});
  const [missingSelectionError, setMissingSelectionError] = useState(false);

  const { mealPlan, isLoading } = useGuestOrderMealPlan({
    dateFrom: initialValues?.date ?? "",
    dateTo: initialValues?.date ?? "",
    enabled: !!initialValues?.date,
  });

  const isEditable = useMemo(() => {
    if (!initialValues?.date) return false;
    const parsed = parseISO(initialValues.date);
    if (isNaN(parsed.getTime())) return false;
    return isOrderableDate(parsed, new Date(), closingTimes);
  }, [initialValues?.date, closingTimes]);

  const initialMeals = useMemo<Record<number, number>>(() => {
    if (!mealPlan || !initialValues?.positions) return {};

    const meals: Record<number, number> = {};

    for (const pos of initialValues.positions) {
      const positionIndex = MENU_CODE_TO_POSITION[pos.menuCode];
      if (positionIndex === undefined) continue;

      for (const day of mealPlan) {
        const menuItem = day.menus.find((m) => m.position === positionIndex);
        if (menuItem) {
          meals[menuItem.id] = pos.quantity;
          break;
        }
      }
    }

    return meals;
  }, [mealPlan, initialValues?.positions]);

  const resetForOrder = useRef<string | null>(null);
  const populatedForOrder = useRef<string | null>(null);

  const EditGuestOrderSchema = createEditGuestOrderSchema(t);

  const formDefaultValues = useMemo<EditGuestOrderFormValues>(
    () => ({
      editor: initialValues?.editor ?? "",
      date: initialValues?.date ?? "",
      comment: initialValues?.comment ?? "",
    }),
    [initialValues],
  );

  const form = useFormWrapper<typeof EditGuestOrderSchema>({
    schema: EditGuestOrderSchema,
    defaultValues: formDefaultValues,
    mode: "onSubmit",
    rethrowSubmitError: true,
  });

  useEffect(() => {
    const currentId = initialValues?.id ?? null;
    // Wipe stale selections as soon as the order changes, even before the
    // meal plan has loaded, so a new order never inherits counters from a previous one.
    if (resetForOrder.current !== currentId) {
      setSelectedMenusCounter({});
      setMissingSelectionError(false);
      form.resetForm(formDefaultValues);
      resetForOrder.current = currentId;
      populatedForOrder.current = null;
    }
    // Apply server-known positions once the plan is loaded. Orders without positions
    // leave the counter empty — that's the correct state for them.
    if (
      populatedForOrder.current !== currentId &&
      Object.keys(initialMeals).length > 0
    ) {
      setSelectedMenusCounter(initialMeals);
      populatedForOrder.current = currentId;
    }
  }, [initialMeals, initialValues?.id, form, formDefaultValues]);

  const allDayMenuItems = useMemo<MenuItemType[] | null>(() => {
    if (!mealPlan || mealPlan.length === 0) return null;
    return mealPlan[0].menus;
  }, [mealPlan]);

  const onSetMenu = (id: number, direction: "plus" | "minus" = "plus") => {
    setSelectedMenusCounter((prev) => {
      const menuCount = prev[id] ?? 0;
      const delta = direction === "plus" ? 1 : -1;
      const next = clampGuestOrderMenuQuantity(menuCount + delta);
      const nextCounter: Record<string, number> = { ...prev, [id]: next };

      if (missingSelectionError && allDayMenuItems) {
        const hasAnySelection = allDayMenuItems.some(
          (menu) => (nextCounter[menu.id] ?? 0) > 0,
        );
        if (hasAnySelection) {
          setMissingSelectionError(false);
          form.clearErrors("root");
        }
      }

      return nextCounter;
    });
  };

  const onSetMenuCount = (id: number, nextCount: number) => {
    setSelectedMenusCounter((prev) => {
      const nextCounter: Record<string, number> = {
        ...prev,
        [id]: clampGuestOrderMenuQuantity(nextCount),
      };

      if (missingSelectionError && allDayMenuItems) {
        const hasAnySelection = allDayMenuItems.some(
          (menu) => (nextCounter[menu.id] ?? 0) > 0,
        );
        if (hasAnySelection) {
          setMissingSelectionError(false);
          form.clearErrors("root");
        }
      }

      return nextCounter;
    });
  };

  const buildPositions = () => {
    if (!allDayMenuItems) return [];
    return allDayMenuItems
      .filter((menu) => (selectedMenusCounter[menu.id] ?? 0) > 0)
      .filter(
        (menu) =>
          menu.position !== undefined && menu.position in POSITION_TO_MENU_CODE,
      )
      .map((menu) => {
        const menuCode = POSITION_TO_MENU_CODE[menu.position];
        const existingPosition = initialValues?.positions?.find(
          (p) => p.menuCode === menuCode,
        );
        return {
          ...(existingPosition ? { id: existingPosition.id } : {}),
          menuCode,
          quantity: selectedMenusCounter[menu.id],
        };
      });
  };

  const onSubmit: Parameters<typeof form.submit>[0] = async ({ values }) => {
    const schoolPath = currentUser?.school;
    if (!schoolPath) {
      form.setError("root", {
        message: t("guestOrders.form.noSchoolAssigned"),
      });
      return;
    }

    const parsed = initialValues?.date ? parseISO(initialValues.date) : null;
    if (
      !parsed ||
      isNaN(parsed.getTime()) ||
      !isOrderableDate(parsed, new Date(), closingTimes)
    ) {
      form.setError("root", {
        message: t("guestOrders.editModal.deadlinePassed"),
      });
      return;
    }

    const positions = buildPositions();
    if (positions.length === 0) {
      setMissingSelectionError(true);
      form.setError("root", {
        message: t("guestOrders.form.noPositionsSelected"),
      });
      return;
    }
    setMissingSelectionError(false);

    const payload = {
      ...(values.comment ? { comment: values.comment } : {}),
      editedBy: values.editor,
      positions,
      servingAt: values.date,
    };

    try {
      await request<SchoolGuestOrder>(
        `${schoolPath}/guest-orders/${initialValues?.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/ld+json" },
          body: JSON.stringify(payload),
        },
      );

      onSubmitSuccess?.();
      onDismiss?.();
    } catch (error) {
      onSubmitError?.(error);
      throw error;
    } finally {
      await queryClient.invalidateQueries({
        queryKey: ["guest-orders", schoolPath],
      });
    }
  };

  const resetForm = () => {
    setSelectedMenusCounter({});
    setMissingSelectionError(false);
    form.resetForm(formDefaultValues);
  };

  const dayHasSelection =
    allDayMenuItems?.some((menu) => (selectedMenusCounter[menu.id] ?? 0) > 0) ??
    false;
  const showDayError = missingSelectionError && !dayHasSelection;

  return (
    <FormProvider form={form}>
      <View style={styles.formContainer}>
        <View style={styles.formElementLeft}>
          <ThemedText>{t("guestOrders.form.dateLabel")}*</ThemedText>
          <View style={styles.formContainer}>
            <View style={styles.formElement}>
              <FormDatePicker
                name="date"
                placeholder={t("guestOrders.form.datePlaceholder")}
                disabled
              />
            </View>
          </View>
          <ThemedText>{t("guestOrders.form.editorLabel")}*</ThemedText>
          <FormTextInput
            name="editor"
            icon={{
              iconSet: "material-community",
              icon: "account-circle-outline",
            }}
            label={t("guestOrders.form.editorPlaceholder")}
          />
          <ThemedText>{t("guestOrders.form.commentLabel")}</ThemedText>
          <FormTextInput
            multiline
            icon={{ iconSet: "material-community", icon: "playlist-edit" }}
            name="comment"
            label={t("guestOrders.form.commentPlaceholder")}
          />
          <View style={[styles.buttonContainer, styles.resetButtonContainer]}>
            <SecondaryButton
              label={t("common.cancel")}
              style={[styles.resetButton]}
              iconName="close"
              iconPosition="left"
              disabled={form.formState.isSubmitting}
              onPress={() => {
                if (form.formState.isSubmitting) return;
                resetForm();
                onDismiss && onDismiss();
              }}
            />
          </View>
        </View>
        <View style={styles.formElementRight}>
          <ThemedText>{t("guestOrders.form.pleaseChooseDishes")}*</ThemedText>
          <ScrollView
            style={initialValues ? styles.limitedScrollView : undefined}
          >
            {isLoading ? (
              <ThemedLoader spinnerSize={50} />
            ) : !isEditable ? (
              <NoticeBox
                title={t("common.notice")}
                textContent={t("guestOrders.editModal.deadlinePassed")}
              />
            ) : allDayMenuItems ? (
              <View style={styles.gap16}>
                <View
                  style={[
                    styles.dayCard,
                    {
                      outlineColor: showDayError
                        ? colors.danger
                        : colors.mutedBorder,
                    },
                    showDayError && styles.dayCardError,
                  ]}
                >
                  <View
                    style={[
                      styles.daySeparator,
                      { backgroundColor: colors.mutedBorder },
                    ]}
                  />
                  <View style={styles.rowCentered}>
                    <ThemedText type="headline" style={styles.flexGrow}>
                      {initialValues?.date &&
                        parseISO(initialValues.date).toLocaleDateString(
                          i18n.language,
                          { day: "2-digit", month: "2-digit", year: "numeric" },
                        )}
                    </ThemedText>
                    <ThemedText
                      type="headline"
                      style={[styles.amountColumn, fonts.regular]}
                    >
                      {t("common.amount")}
                    </ThemedText>
                  </View>
                  <View style={styles.gap16}>
                    {allDayMenuItems.map((menu) => (
                      <View key={menu.id} style={styles.menuRow}>
                        <View style={styles.menuItemContainer}>
                          <MenuItem item={menu} />
                        </View>
                        <View style={styles.counterRow}>
                          <Button
                            buttonStyle={[
                              styles.squareIconButton,
                              { borderColor: colors.mutedBorder },
                            ]}
                            iconName="minus"
                            onPress={() => onSetMenu(menu.id, "minus")}
                          />
                          <GuestOrderMenuQuantityInput
                            value={selectedMenusCounter[menu.id] ?? 0}
                            onChange={(value) => onSetMenuCount(menu.id, value)}
                          />
                          <Button
                            buttonStyle={[
                              styles.squareIconButton,
                              { borderColor: colors.mutedBorder },
                            ]}
                            iconName="plus"
                            onPress={() => onSetMenu(menu.id, "plus")}
                          />
                        </View>
                      </View>
                    ))}
                  </View>
                </View>

                <View></View>
              </View>
            ) : (
              <NoticeBox
                title={t("common.notice")}
                textContent={t("guestOrders.noMealPlanNotice")}
              />
            )}
          </ScrollView>

          <FormError />
          <View style={[styles.buttonContainer, styles.justifyEnd]}>
            <SubmitButton
              title={t("guestOrders.editModal.submitButtonText")}
              disabled={
                form.formState.isSubmitting ||
                isLoading ||
                !isEditable ||
                allDayMenuItems === null
              }
              onPress={() => {
                void form.submit(onSubmit).catch(() => undefined);
              }}
              buttonStyle={[
                styles.submitButton,
                { backgroundColor: colors.border },
              ]}
              buttonTextStyle={[
                styles.buttonText,
                { color: colors.background, ...fonts.bold },
              ]}
              iconName="arrow-right"
              iconPosition="right"
              iconColor={colors.background}
            />
          </View>
        </View>
      </View>
    </FormProvider>
  );
}

const styles = StyleSheet.create({
  submitButton: {
    borderRadius: 56,
    height: 56,
    marginTop: 24,
    paddingHorizontal: 16,
    gap: 0,
  },
  resetButton: {
    marginTop: 24,
  },
  buttonText: {
    fontSize: 16,
    paddingHorizontal: 8,
  },
  formContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    gap: 32,
  },
  formElement: {
    flex: 1,
    gap: 8,
  },
  formElementLeft: {
    flex: 6,
    gap: 8,
  },
  formElementRight: {
    flex: 7,
    gap: 8,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  justifyEnd: {
    justifyContent: "flex-end",
  },
  gap16: {
    gap: 16,
  },
  dayCardError: {
    outlineWidth: 2,
    outlineOffset: -2,
  },
  dayCard: {
    flexDirection: "column",
    gap: 16,
    outlineWidth: 1,
    outlineOffset: -1,
    outlineStyle: "solid",
    padding: 16,
    borderRadius: 16,
    marginBottom: 16,
    position: "relative",
  },
  daySeparator: {
    position: "absolute",
    top: 0,
    bottom: 0,
    right: GUEST_ORDER_AMOUNT_COLUMN_SEPARATOR_OFFSET,
    width: 1,
  },
  rowCentered: {
    flexDirection: "row",
    gap: 16,
    alignItems: "center",
  },
  flexGrow: {
    flexGrow: 1,
  },
  amountColumn: {
    width: GUEST_ORDER_AMOUNT_COLUMN_WIDTH,
  },
  menuRow: {
    flexDirection: "row",
    gap: 16,
    alignItems: "center",
    justifyContent: "space-between",
  },
  menuItemContainer: {
    flexGrow: 1,
    paddingRight: 16,
    flexShrink: 1,
    minWidth: 0,
  },
  counterRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 8,
  },
  squareIconButton: {
    borderWidth: 1,
    paddingHorizontal: 0,
    justifyContent: "center",
    height: 44,
    width: 44,
    minWidth: 44,
  },
  limitedScrollView: {
    maxHeight: 600,
  },
  resetButtonContainer: {
    flexGrow: 1,
    alignItems: "flex-end",
  },
});
